import type {
  BatchReleaseFullResponse,
  BatchReleaseResponse,
  BatchReleaseSelectedResponse,
  BatchReserveResponse,
} from '@ooh/contracts';
import type { MultiCellSelection, MultiSelectCellRef } from '../lib/availability-types';
import type {
  UnifiedSelectCellResolution,
  UnifiedSelectOwnReservation,
} from '../lib/unified-select-types';
import { isHoldRangeUnderCovered } from '../components/availability-cell-visual';
import type { ResolvedSelection, UnifiedSelectPopoverOfferScope } from './popover-types';

export function resolveSelection(
  selection: MultiCellSelection,
  resolveCell: (ref: MultiSelectCellRef) => UnifiedSelectCellResolution | null,
): ResolvedSelection {
  const unitIds = new Set<string>();
  const freeCells: UnifiedSelectCellResolution[] = [];
  const heldByOtherCells: UnifiedSelectCellResolution[] = [];
  const releasableCells: ResolvedSelection['releasableCells'] = [];

  for (const ref of selection.cells) {
    const resolved = resolveCell(ref);
    if (resolved === null) continue;
    unitIds.add(ref.unitId);
    if (resolved.ownReservation) {
      releasableCells.push({ ...resolved, ownReservation: resolved.ownReservation });
    } else if (resolved.heldByOther) {
      heldByOtherCells.push(resolved);
    } else {
      freeCells.push(resolved);
    }
  }

  return {
    unitCount: unitIds.size,
    cellCount: freeCells.length + heldByOtherCells.length + releasableCells.length,
    freeCells,
    heldByOtherCells,
    releasableCells,
  };
}

interface ActionGates {
  effectiveOfferId: number | null;
  canSubmitReserve: boolean;
  canRelease: boolean;
}

export function computeActionGates(
  canManage: boolean,
  offerScope: UnifiedSelectPopoverOfferScope | null,
  chosenOfferId: number | null,
  counts: { free: number; releasable: number; heldByOther: number },
  pending: { reserve: boolean; release: boolean },
): ActionGates {
  const effectiveOfferId = chosenOfferId ?? offerScope?.offerId ?? null;
  return {
    effectiveOfferId,
    canSubmitReserve: canManage && effectiveOfferId !== null && counts.free > 0 && !pending.reserve,
    // Enabled for a releasable own hold OR any cell the caller COULD hold a queue entry on: the
    // grid never fetches queue membership for a rectangle spanning many units, so it cannot rule
    // this out.
    canRelease: canManage && (counts.releasable > 0 || counts.heldByOther > 0) && !pending.release,
  };
}

/**
 * Shared `{ mediaUnitId, dateFrom, dateTo }` cell mapping for batch request bodies. A
 * `dateRangeOverride` (only set when the caller edited the Rezerwacja dialog's date range)
 * replaces every cell's own range uniformly, like `ttlExpiresAt` applies batch-wide.
 */
export function toBatchCells(
  cells: UnifiedSelectCellResolution[],
  dateRangeOverride?: { from: Date; to: Date },
): Array<{ mediaUnitId: number; dateFrom: Date; dateTo: Date }> {
  return cells.map((cell) => ({
    mediaUnitId: cell.mediaUnitId,
    dateFrom: dateRangeOverride?.from ?? cell.dateFrom,
    dateTo: dateRangeOverride?.to ?? cell.dateTo,
  }));
}

// A reverse deploy briefly pairs a new bundle with an old API, so a response can lack keys its
// type claims are always present. The normalizers below read those as empty instead of throwing.

export function normalizeBatchReserveResponse(
  data: Partial<BatchReserveResponse> | null | undefined,
): BatchReserveResponse {
  return {
    created: data?.created ?? [],
    queued: data?.queued ?? [],
    conflicted: data?.conflicted ?? [],
  };
}

export function normalizeBatchReleaseResponse(
  data: Partial<BatchReleaseFullResponse> | null | undefined,
): BatchReleaseFullResponse {
  return {
    released: data?.released ?? [],
    skipped: data?.skipped ?? [],
    queuesLeft: data?.queuesLeft ?? 0,
  };
}

/**
 * `BatchReleaseResponse` is a plain union without a discriminant: `mode='full'` carries
 * `released`, `mode='selected'` carries `outcomes`. Reading a selected-mode result through
 * `normalizeBatchReleaseResponse` would silently treat it as empty.
 */
export function isSelectedReleaseResponse(
  data: BatchReleaseResponse | null | undefined,
): data is BatchReleaseSelectedResponse {
  return data != null && 'outcomes' in data;
}

export function normalizeBatchReleaseSelectedResponse(
  data: Partial<BatchReleaseSelectedResponse> | null | undefined,
): BatchReleaseSelectedResponse {
  return {
    outcomes: data?.outcomes ?? [],
    skipped: data?.skipped ?? [],
    queuesLeft: data?.queuesLeft ?? 0,
  };
}

/**
 * True when the selection UNDER-COVERS at least one own hold: the selected cells' min-to-max span
 * (grouped per reservation id) leaves part of that hold's own window unselected. Gates Zwolnij's
 * whole-versus-selected-period choice. A hold without `holdDateFrom`/`holdDateTo` counts as NOT
 * under-covered, since coverage cannot be determined and whole-release is the safe default.
 */
export function hasUnderCoveredHold(
  releasableCells: ResolvedSelection['releasableCells'],
): boolean {
  const spans = new Map<number, { min: Date; max: Date; hold: UnifiedSelectOwnReservation }>();
  for (const cell of releasableCells) {
    const id = cell.ownReservation.id;
    const existing = spans.get(id);
    if (!existing) {
      spans.set(id, { min: cell.dateFrom, max: cell.dateTo, hold: cell.ownReservation });
    } else {
      if (cell.dateFrom < existing.min) existing.min = cell.dateFrom;
      if (cell.dateTo > existing.max) existing.max = cell.dateTo;
    }
  }
  for (const { min, max, hold } of spans.values()) {
    if (!hold.holdDateFrom || !hold.holdDateTo) continue;
    const holdFrom = new Date(`${hold.holdDateFrom}T00:00:00.000Z`);
    const holdTo = new Date(`${hold.holdDateTo}T00:00:00.000Z`);
    if (isHoldRangeUnderCovered(min, max, holdFrom, holdTo)) return true;
  }
  return false;
}
